import { useState, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import { isHeic, heicTo } from 'heic-to'
import { toast } from 'react-toastify'
import { FaRegTrashCan } from 'react-icons/fa6'

import usePhoto from '@hooks/usePhoto'
import useStudents from '@hooks/useStudents'
import useOnboarding from '@hooks/useOnboarding'
import {
  sendUploadPhotoEvent,
  sendUploadPhotoFileSelectedEvent,
  sendUploadPhotoLargeSize,
  sendUploadPhotoEditorClosedEvent,
  sendUploadPhotoSuccessEvent,
  sendUploadPhotoErrorEvent,
  sendUploadPhotoDeletedEvent,
} from '@/services/analytics/gtm'

import PhotoEdit from '@components/PhotoEdit'
import Photo from '@components/Photo'
import Button from '@components/Button'
import Spinner from '@components/Spinner'
import Modal from '@components/Modal'

export default function PhotoUpload() {
  const { t } = useTranslation()
  const hiddenFileInput = useRef<HTMLInputElement | null>(null)
  const [uploadedImage, setUploadedImage] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [showEditorModal, setShowEditorModal] = useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)

  const { photo, setPhoto, isLoading, deletePhoto } = usePhoto()
  const { students } = useStudents()
  const { isOnboarding } = useOnboarding()

  const handleUploadButtonClick = () => {
    sendUploadPhotoEvent({
      isOnboarding: isOnboarding ?? false,
      isReplacing: !!photo,
    })
    if (hiddenFileInput?.current) {
      hiddenFileInput.current.click()
    }
  }

  async function selectFile(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target && e.target.files) {
      const file = e.target.files[0]
      sendUploadPhotoFileSelectedEvent(file.type)

      if (file.size > 10485760) {
        // 10 MB
        sendUploadPhotoLargeSize(file.size)
      }

      if (await isHeic(file)) {
        const jpgBlob = await heicTo({
          blob: file,
          type: 'image/jpeg',
          quality: 1,
        })

        const jpg = URL.createObjectURL(jpgBlob)
        setUploadedImage(jpg)
        setShowEditorModal(true)
      } else {
        const reader = new FileReader()

        //set the onload handler which is called when readAsDataURL completes
        reader.onload = () => {
          setUploadedImage(reader.result as string)
          setShowEditorModal(true)
        }

        //read the uploaded file into a base64 string, then trigger reader.onload()
        reader.readAsDataURL(file)
      }
    }
  }

  function uploadEditedPhotoFile(photo: File) {
    setIsUploading(true)
    setPhoto({
      photo,
      onMutationSuccess: () => {
        sendUploadPhotoSuccessEvent(photo.size)
        toast.success('Your family photo was updated!')
      },
      onMutationError: (error: Error) => {
        sendUploadPhotoErrorEvent(photo.size)
        toast.error(
          error instanceof Error
            ? error.message
            : `Sorry! Something went wrong updating your family photo. Please try again.`,
          {
            autoClose: false,
          },
        )
        Sentry.logger.error('Photo upload error', { error: String(error) })
      },
      onMutationSettled: () => {
        setTimeout(() => {
          setIsUploading(false)
          setShowEditorModal(false)
        }, 1000)
      },
    })
  }

  function handleEditorClose() {
    sendUploadPhotoEditorClosedEvent()
    setShowEditorModal(false)
  }

  function handleDeletePhoto() {
    setIsUploading(true)
    deletePhoto({
      onMutationSuccess: () => {
        sendUploadPhotoDeletedEvent()
        toast.success('Your family photo was deleted.')
      },
      onMutationError: (error: Error) => {
        toast.error(
          error instanceof Error
            ? error.message
            : `Sorry! Something went wrong deleting your family photo. Please try again.`,
          {
            autoClose: false,
          },
        )
        Sentry.logger.error('Photo delete error', { error: String(error) })
      },
      onMutationSettled: () => {
        setTimeout(() => {
          setIsUploading(false)
          setShowDeleteModal(false)
        }, 1000)
      },
    })
  }

  if (isUploading || isLoading) {
    return (
      <div className='flex h-50 my-2.5 flex-row items-center justify-center'>
        <Spinner />
      </div>
    )
  }

  return (
    <div id='upload-photo'>
      <PhotoEdit
        imageSrc={uploadedImage}
        open={showEditorModal}
        onSavePhoto={uploadEditedPhotoFile}
        onClose={handleEditorClose}
      />

      <Modal
        variant='danger'
        title='Delete Photo'
        message='Are you sure you want to delete this photo?'
        buttonLabel='Delete Photo'
        show={showDeleteModal}
        onConfirm={handleDeletePhoto}
        onClose={() => setShowDeleteModal(false)}
      />

      <input className='hidden' name='photo' type='file' onChange={selectFile} ref={hiddenFileInput} accept='image/*' />

      <div className='flex flex-col items-center mb-2'>
        <h2 className='text-lg font-bold text-neutral-800 mb-2 text-center font-serif'>
          {t('content.components.upload_photo.title')}
        </h2>
        <div className='mb-10 font-sans font-normal text-sm text-gray-700 text-center'>
          {t('content.components.upload_photo.subtitle', { plural: students && students.length > 1 ? 's' : '' })}
        </div>
        <div>
          <div className={`flex flex-row justify-center space-x-2 ${photo ? 'ml-1' : 'mx-auto'}`}>
            <div onClick={handleUploadButtonClick}>
              <Photo />
            </div>
            {photo && (
              <FaRegTrashCan
                className='self-end -ml-6 w-5 h-5 mt-0.5 rounded-md text-neutral-500 hover:text-error hover:cursor-pointer active:text-error'
                onClick={() => setShowDeleteModal(true)}
              />
            )}
          </div>

          <div className='flex flex-row justify-center space-x-2 mt-4'>
            <Button
              variant='cta'
              onClick={handleUploadButtonClick}
              label={t('content.components.upload_photo.title')}
            />
          </div>
        </div>
      </div>
      <div className='text-center font-serif text-xs text-secondary-txt pt-4 w-4/5 mx-auto'>
        {t('content.components.upload_photo.body_text')}
      </div>
    </div>
  )
}
